/**
 * Layaway contract total preview — mirrors server buildLayawayTaxSectionUnfinalized +
 * finalizeSectionTaxPerBucketNatural for delete/edit refund warnings.
 */

function layawayNum(value) {
	const amount = Number(value);
	return Number.isFinite(amount) ? amount : 0;
}

function layawayBool(value) {
	if (value === true || value === 1 || value === '1' || value === 'true') return true;
	if (value === false || value === 0 || value === '0' || value === 'false') return false;
	return !!value;
}

function layawayLocationCombinedRate(location) {
	return (
		layawayNum(location?.tax_rate_city ?? location?.TaxRateCity) +
		layawayNum(location?.tax_rate_county ?? location?.TaxRateCounty) +
		layawayNum(location?.tax_rate_special ?? location?.TaxRateSpecial) +
		layawayNum(location?.tax_rate_state ?? location?.TaxRateState)
	);
}

function layawayLocationFflRate(location) {
	return layawayNum(location?.tax_rate_fflboss ?? location?.TaxRateFflboss);
}

function layawayEntrySkuClass(entry) {
	const sku = entry?.information?.skus ?? entry?.information?.Skus ?? null;
	if (!sku) {
		return null;
	}
	return sku.sku_classes ?? sku.skuClasses ?? sku.SkuClasses ?? null;
}

function layawayEntryQuantity(entry) {
	return layawayNum(entry?.quantity ?? entry?.information?.quantity ?? 1) || 1;
}

function layawayEntryUnitCost(entry) {
	return layawayNum(
		entry?.price ??
		entry?.cost ??
		entry?.information?.price ??
		entry?.information?.skus?.price_retail ??
		entry?.information?.price_list
	);
}

function layawayEntryDiscount(entry) {
	return layawayNum(entry?.discount_amount ?? 0);
}

function layawayEntryTaxable(entry) {
	if (entry?.taxable != null) {
		return layawayBool(entry.taxable);
	}
	if (entry?.information?.taxable != null) {
		return layawayBool(entry.information.taxable);
	}
	if (entry?.type === 'item' || entry?.type === 'layaway_item') {
		const sku = entry?.information?.skus ?? entry?.information?.Skus ?? null;
		return sku?.taxable != null ? layawayBool(sku.taxable) : true;
	}
	if (entry?.type === 'product' || entry?.type === 'layaway_product') {
		return entry?.information?.taxable != null ? layawayBool(entry.information.taxable) : true;
	}
	if (entry?.type === 'service' || entry?.type === 'layaway_service') {
		return entry?.information?.taxable != null ? layawayBool(entry.information.taxable) : true;
	}
	return true;
}

/**
 * Match Utilities::effectiveLayawayItemsLineTaxSpec — persisted line tax_rate wins.
 */
export function layawayEntryLineTaxSpec(entry, location, isFflLinked) {
	const locationRate = layawayLocationCombinedRate(location);
	const fflBossRate = layawayLocationFflRate(location);
	const entryType = String(entry?.type || 'item').toLowerCase();

	let group = 'Default Group';
	let rate = locationRate;

	if (entryType === 'consignment' || entryType === 'layaway_consignment') {
		group = 'Consignments';
		rate = locationRate;
	} else if (entryType === 'product' || entryType === 'layaway_product') {
		group = 'FFLBoss';
		if (isFflLinked && fflBossRate > 0) {
			rate = fflBossRate;
		}
	} else if (entryType === 'service' || entryType === 'layaway_service') {
		group = 'Services';
		rate = locationRate;
	} else {
		const skuClass = layawayEntrySkuClass(entry);
		const shouldOverride = layawayBool(skuClass?.should_override);
		const overrideRate = layawayNum(skuClass?.tax_override);
		if (shouldOverride && overrideRate > 0) {
			rate = overrideRate;
		}
		group = skuClass?.name || 'Default Group';
	}

	const storedRate = layawayNum(entry?.tax_rate);
	if (storedRate > 0.005) {
		rate = storedRate;
	}

	return {group, rate};
}

export function computeLayawayContractTotals(entries, location, isFflLinked) {
	const safeEntries = Array.isArray(entries) ? entries : [];
	const locationRate = layawayLocationCombinedRate(location);
	const taxSummaryMap = {};
	let subtotal = 0;

	safeEntries.forEach((entry) => {
		const quantity = layawayEntryQuantity(entry);
		const unitCost = layawayEntryUnitCost(entry);
		const discount = layawayEntryDiscount(entry);
		const lineAmount = Math.max(0, (unitCost * quantity) - discount);
		subtotal += lineAmount;

		if (!layawayEntryTaxable(entry) || lineAmount <= 0) {
			return;
		}

		const {group, rate} = layawayEntryLineTaxSpec(entry, location, isFflLinked);
		const key = `${group}|${rate.toFixed(4)}`;
		if (!taxSummaryMap[key]) {
			taxSummaryMap[key] = {group, rate, amount: 0, quantity: 0};
		}
		taxSummaryMap[key].amount += lineAmount;
		taxSummaryMap[key].quantity += quantity;
	});

	const taxSummary = Object.values(taxSummaryMap).map((row) => {
		const amount = Math.round((row.amount + Number.EPSILON) * 100) / 100;
		return {
			group: row.group,
			rate: row.rate,
			amount,
			quantity: row.quantity,
			tax: Math.round((amount * row.rate / 100 + Number.EPSILON) * 100) / 100,
		};
	});

	const taxAmount = Math.round((taxSummary.reduce((sum, row) => sum + row.tax, 0) + Number.EPSILON) * 100) / 100;
	const subTotalAmount = Math.round((subtotal + Number.EPSILON) * 100) / 100;
	const amount = Math.round((subTotalAmount + taxAmount + Number.EPSILON) * 100) / 100;

	return {
		sub_total_amount: subTotalAmount,
		tax_amount: taxAmount,
		amount,
		tax_summary: taxSummary,
		tax_json: JSON.stringify(taxSummary),
		header_display_tax_rate: locationRate,
	};
}
